<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    items?: any[];
    returnObject?: boolean;
    itemValue?: string;
    itemTitle?: string;
    loading?: boolean;
    disabled?: boolean;
    hideDetails?: boolean | 'auto';
    errors?: string | string[] | any;
  }>(),
  {
    itemValue: '',
    itemTitle: '',
    placeholder: '',
    returnObject: false,
    items: () => [],
    errors: () => [],
    loading: false,
    disabled: false,
    hideDetails: false,
  }
);

const emits = defineEmits<{
  (e: 'remove', item: any): void;
  (e: 'search', value: string): void;
}>();

const selectedItems = defineModel();

// Handle error messages properly to prevent [object Object] display
const errorMessages = computed(() => {
  if (!props.errors) return [];
  
  if (Array.isArray(props.errors)) {
    return props.errors.map((error: any) => {
      // Handle Vuelidate error objects
      if (error && typeof error === 'object' && error.$message) {
        return error.$message;
      }
      // Handle other objects
      if (typeof error === 'object') {
        return JSON.stringify(error);
      }
      // Handle strings and other types
      return String(error);
    });
  }
  
  // Handle single error
  if (typeof props.errors === 'object') {
    // Handle Vuelidate error object
    if (props.errors.$message) {
      return [props.errors.$message];
    }
    return [JSON.stringify(props.errors)];
  }
  
  return [String(props.errors)];
});
</script>

<template>
  <div class="d-block w-full">
    <v-autocomplete
      v-model="selectedItems"
      :items="items"
      :error-messages="errorMessages"
      variant="solo-filled"
      flat
      multiple
      color="primary"
      :item-title="itemTitle"
      :item-value="itemValue"
      :return-object="returnObject"
      :placeholder="placeholder"
      :loading="loading"
      :disabled="disabled"
      :hide-details="hideDetails"
      @update:search="emits('search', $event)"
    >
      <template #selection></template>
    </v-autocomplete>

    <v-chip-group :model-value="selectedItems" column data-testid="selectedItems">
      <v-chip
        v-for="(item, index) in selectedItems"
        :key="`${!!itemValue ? item[itemValue] : item}${index}`"
        :clickable="false"
        :text="itemTitle ? item[itemTitle] : item"
        :disabled="disabled"
        variant="outlined"
        closable
        @click:close="emits('remove', item)"
      >
        <template #close>
          <v-icon>mdi-close</v-icon>
        </template>
      </v-chip>
    </v-chip-group>
  </div>
</template>
